/* Module « Épisodes du podcast » (page Écouter + accueil). Sans dépendance, aucun script tiers. Maquette : js/podcast.js.
   Rendu serveur : snippets/mi-podcast-module.liquid (liste de secours complète, lisible sans JavaScript) + bloc <script type="application/json" data-pod-config>
   (URL du flux, textes traduits, épisodes de secours). Ce module remplace la liste de secours par la liste construite depuis le flux RSS dès qu'il est lisible.
   Flux : lecture dans le navigateur (l'hébergeur autorise : access-control-allow-origin *), cache de session 1 h ; échec ou flux vide : la liste de secours reste en place.
   Un seul <audio> pour toute la page : un seul épisode actif à la fois, aucun chargement avant le clic (preload none).
   Réinitialisé dans l'éditeur de thème (shopify:section:load). Aucun texte en dur : tous les libellés viennent du bloc de configuration (clés podcast.* des locales). */

const SPEEDS = [1, 1.25, 1.5, 2];
const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const ICON = {
  play: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 6.6v10.8a.6.6 0 0 0 .92.5l8.4-5.4a.6.6 0 0 0 0-1L9.92 6.1A.6.6 0 0 0 9 6.6z" fill="currentColor"/></svg>',
  pause: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="7" y="6" width="3.4" height="12" rx=".6" fill="currentColor"/><rect x="13.6" y="6" width="3.4" height="12" rx=".6" fill="currentColor"/></svg>',
  back: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 5a7 7 0 1 1-6.6 4.7M12 5V2L8 5.5 12 9" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><text x="12" y="16.2" font-size="6.4" font-family="Jost,sans-serif" text-anchor="middle" fill="currentColor">15</text></svg>',
  fwd: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 5a7 7 0 1 0 6.6 4.7M12 5V2l4 3.5L12 9" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><text x="12" y="16.2" font-size="6.4" font-family="Jost,sans-serif" text-anchor="middle" fill="currentColor">30</text></svg>',
};

/* ---------- Textes (configuration rendue par Liquid) ---------- */
const format = (tpl, vars = {}) => String(tpl || '').replace(/\{(\w+)\}/g, (_, k) => (k in vars ? vars[k] : ''));

/* ---------- Données ---------- */
/* Titres du flux : « partie 2 » entre parenthèses, tirets typographiques (même règle que la maquette). */
const tidy = (s) => String(s || '').replace(/\s*[-–—:]?\s*\(?\b(partie|part)\s*(\d+)\)?\s*$/i, (m, w, n) => ` (${/^partie/i.test(w) ? 'partie' : 'part'} ${n})`).replace(/ [+\-] /g, ' \u2013 ').replace(/'/g, '\u2019');
const clean = (s) => String(s || '').replace(/<[^>]*>/g, ' ').replace(/https?:\/\/\S+/g, '').replace(/\S+@\S+/g, '').replace(/\s+/g, ' ').trim();
const toSeconds = (v) => { if (!v) return 0; const p = String(v).trim().split(':').map(Number); if (p.some(isNaN)) return 0; return p.reduce((a, n) => a * 60 + n, 0); };

/* Numéro : entier, « FI 2 » (série Femme Intentionnelle) ou null (hors série) */
function normaliseNumber(n) {
  const s = String(n ?? '').trim();
  if (!s) return null;
  if (/^\d+$/.test(s)) return +s;
  const fi = /FI\s*(\d+)/i.exec(s);
  return fi ? 'FI ' + fi[1] : null;
}

function parseNumber(title) {
  const m = /^\s*(?:#\s*)?(FI)\s*(\d+)\s*[|:\-–]?\s*/i.exec(title) || /^\s*(\d{1,3})\s*\|\s*/.exec(title);
  if (!m) return { numero: null, titre: title.trim() };
  return m[2] ? { numero: 'FI ' + m[2], titre: title.slice(m[0].length).trim() } : { numero: +m[1], titre: title.slice(m[0].length).trim() };
}

function parseFeed(xml) {
  const doc = new DOMParser().parseFromString(xml, 'text/xml');
  if (doc.querySelector('parsererror')) throw new Error('flux illisible');
  const items = [...doc.querySelectorAll('item')].map((it) => {
    const get = (n) => it.querySelector(n)?.textContent?.trim() || '';
    const enc = it.querySelector('enclosure');
    const { numero, titre } = parseNumber(get('title'));
    const d = new Date(get('pubDate'));
    return {
      id: get('guid'), numero, titre,
      date_iso: isNaN(d) ? '' : d.toISOString(),
      duree_s: toSeconds(it.getElementsByTagName('itunes:duration')[0]?.textContent),
      audio_url: enc?.getAttribute('url') || '',
    };
  }).filter((e) => e.audio_url && e.titre);
  if (!items.length) throw new Error('flux vide');
  return items;
}

/* Complète les épisodes du flux avec les liens et la description nettoyée des épisodes de secours (même identifiant, sinon même numéro) */
function merge(list, fallback) {
  const byId = new Map(fallback.map((e) => [e.id, e]));
  const byNum = new Map(fallback.filter((e) => e.numero !== null).map((e) => [String(e.numero), e]));
  return list.map((e) => {
    const f = byId.get(e.id) || (e.numero !== null ? byNum.get(String(e.numero)) : null);
    return f ? { ...e, description: f.description, apple_url: f.apple_url, spotify_url: f.spotify_url } : e;
  });
}

async function loadEpisodes(cfg, fallback) {
  const feed = cfg.feed;
  if (feed) {
    const key = 'mi-pod:' + feed;
    try {
      const c = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (c && Date.now() - c.t < 3600e3) return { list: merge(c.list, fallback), source: 'feed' };
    } catch (e) { /* cache de session indisponible */ }
    try {
      const ctl = new AbortController();
      const to = setTimeout(() => ctl.abort(), 7000);
      const r = await fetch(feed, { signal: ctl.signal });
      clearTimeout(to);
      if (!r.ok) throw new Error('http ' + r.status);
      const list = parseFeed(await r.text());
      try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), list: list.slice(0, 30) })); } catch (e) { /* ignoré */ }
      return { list: merge(list, fallback), source: 'feed' };
    } catch (e) { /* repli ci-dessous */ }
  }
  return { list: fallback, source: 'json' };
}

/* ---------- Mise en forme ---------- */
function makeFormat(cfg) {
  const T = (name, vars) => format(cfg.i18n[name], vars);
  const loc = cfg.locale === 'en' ? 'en-GB' : 'fr-FR';
  const dec = cfg.locale === 'en' ? '.' : ',';
  return {
    T,
    fmtDate: (iso) => { if (!iso) return ''; const d = new Date(iso); return isNaN(d) ? '' : new Intl.DateTimeFormat(loc, { day: 'numeric', month: 'long', year: 'numeric' }).format(d); },
    fmtMin: (s) => T('min', { n: Math.max(1, Math.round(s / 60)) }),
    fmtClock: (s) => { s = Math.max(0, Math.floor(s || 0)); const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); const ss = s % 60; return (h ? h + ':' + String(m).padStart(2, '0') : m) + ':' + String(ss).padStart(2, '0'); },
    fmtSpeech: (s) => { s = Math.floor(s || 0); return `${Math.floor(s / 60)} min ${s % 60} s`; },
    fmtSpeed: (v) => String(v).replace('.', dec) + '×',
    numLabel: (e) => {
      if (typeof e.numero === 'number') return T('episode', { n: e.numero });
      if (typeof e.numero === 'string') return T('serieFi', { n: e.numero.replace(/\D/g, '') });
      return T('horsSerie');
    },
  };
}

/* ---------- Lecteur unique partagé ---------- */
const audio = new Audio();
audio.preload = 'none';
let current = null;   // { ui }
/* Un seul média actif sur la page Média : le démarrage de l'audio coupe les vidéos YouTube (assets/mi-media.js), et réciproquement. */
audio.addEventListener('play', () => document.dispatchEvent(new CustomEvent('mi:media-start', { detail: 'podcast' })));
document.addEventListener('mi:media-start', (e) => { if (e.detail !== 'podcast' && !audio.paused) audio.pause(); });

function setBtnState(ui, playing) {
  const { T } = ui.fmt;
  ui.play.setAttribute('aria-pressed', String(playing));
  ui.play.innerHTML = playing ? ICON.pause : ICON.play;
  const numbered = typeof ui.ep.numero === 'number';
  ui.play.setAttribute('aria-label', playing ? T('pause', { titre: ui.ep.titre }) : (numbered ? T('lire', { n: ui.ep.numero, titre: ui.ep.titre }) : T('lireCourt', { titre: ui.ep.titre })));
  ui.row.classList.toggle('is-playing', playing);
}

function updateProgress(ui) {
  const { T, fmtClock, fmtSpeech } = ui.fmt;
  const dur = isFinite(audio.duration) && audio.duration > 0 ? audio.duration : ui.ep.duree_s;
  ui.range.max = String(Math.floor(dur) || 0);
  ui.range.value = String(Math.floor(audio.currentTime));
  const pct = dur ? Math.min(100, (audio.currentTime / dur) * 100) : 0;
  ui.range.style.setProperty('--pod-p', pct + '%');
  ui.range.setAttribute('aria-valuetext', T('valeur', { a: fmtSpeech(audio.currentTime), b: fmtSpeech(dur) }));
  ui.cur.textContent = fmtClock(audio.currentTime);
  ui.tot.textContent = fmtClock(dur);
}

function deactivate(keepTime) {
  if (!current) return;
  const { ui } = current;
  setBtnState(ui, false);
  ui.row.classList.remove('is-active', 'is-loading', 'has-error');
  if (!ui.row.classList.contains('is-featured')) ui.panel.hidden = true;
  ui.range.disabled = true; ui.range.value = '0'; ui.range.style.setProperty('--pod-p', '0%');
  ui.cur.textContent = '0:00'; ui.tot.textContent = ui.fmt.fmtClock(ui.ep.duree_s);
  ui.error.hidden = true;
  if (!keepTime) audio.pause();
  current = null;
}

function activate(ui) {
  if (current && current.ui !== ui) { audio.pause(); deactivate(true); }
  if (current && current.ui === ui) return;
  current = { ui };
  ui.row.classList.add('is-active', 'is-loading');
  ui.panel.hidden = false;
  ui.range.disabled = false;
  audio.src = ui.ep.audio_url;
  audio.playbackRate = ui.speed;
  ui.error.hidden = true;
  audio.play().catch(() => {});
}

function toggle(ui) {
  if (current && current.ui === ui) { if (audio.paused) audio.play().catch(() => {}); else audio.pause(); } else activate(ui);
}

audio.addEventListener('playing', () => { if (current) { current.ui.row.classList.remove('is-loading'); setBtnState(current.ui, true); } });
audio.addEventListener('pause', () => { if (current) setBtnState(current.ui, false); });
audio.addEventListener('waiting', () => { if (current) current.ui.row.classList.add('is-loading'); });
audio.addEventListener('canplay', () => { if (current) current.ui.row.classList.remove('is-loading'); });
audio.addEventListener('loadedmetadata', () => { if (current) updateProgress(current.ui); });
audio.addEventListener('durationchange', () => { if (current) updateProgress(current.ui); });
audio.addEventListener('timeupdate', () => { if (current) updateProgress(current.ui); });
audio.addEventListener('ended', () => { if (current) { const ui = current.ui; audio.currentTime = 0; setBtnState(ui, false); updateProgress(ui); } });
audio.addEventListener('error', () => { if (current) { const ui = current.ui; ui.row.classList.remove('is-loading'); ui.row.classList.add('has-error'); ui.error.hidden = false; setBtnState(ui, false); } });

/* ---------- Rendu ---------- */
function rowHtml(e, i, featured, compact, fmt, cfg) {
  const { T, fmtDate, fmtMin, fmtClock, numLabel } = fmt;
  const links = [];
  if (e.apple_url) links.push(`<a href="${esc(e.apple_url)}" target="_blank" rel="noopener">Apple Podcasts</a>`);
  if (e.spotify_url && /\/episode\//.test(e.spotify_url)) links.push(`<a href="${esc(e.spotify_url)}" target="_blank" rel="noopener">Spotify</a>`);
  const desc = clean(e.description);
  return `<li class="pod-ep${featured ? ' is-featured' : ''}" data-i="${i}">
  <div class="pod-ep__head">
    <button type="button" class="pod-play" aria-pressed="false">${ICON.play}</button>
    <div class="pod-ep__meta">
      <p class="pod-ep__num">${esc(numLabel(e))}</p>
      <h3 class="pod-ep__title">${esc(tidy(e.titre))}</h3>
      <p class="pod-ep__info"><span>${esc(fmtDate(e.date_iso))}</span><span>${esc(e.duree_s ? fmtMin(e.duree_s) : '')}</span></p>
    </div>
  </div>
  <div class="pod-ep__panel"${featured ? '' : ' hidden'}>
    <div class="pod-bar">
      <span class="pod-time pod-time--cur">0:00</span>
      <input class="pod-range" type="range" min="0" max="${e.duree_s || 0}" value="0" step="1" disabled aria-label="${esc(T('progression'))}">
      <span class="pod-time pod-time--tot">${fmtClock(e.duree_s)}</span>
    </div>
    <div class="pod-tools">
      <button type="button" class="pod-tool pod-tool--back" aria-label="${esc(T('reculer'))}">${ICON.back}</button>
      <button type="button" class="pod-tool pod-tool--fwd" aria-label="${esc(T('avancer'))}">${ICON.fwd}</button>
      <button type="button" class="pod-tool pod-tool--speed"></button>
      ${links.length && !compact ? `<p class="pod-also">${esc(T('aussi'))} ${links.join(' · ')}</p>` : ''}
    </div>
    ${desc.length > 24 && !compact ? `<p class="pod-desc">${esc(desc)}</p>` : ''}
    <p class="pod-error" role="alert" hidden>${esc(T('erreurLecture'))} <a href="${esc(e.apple_url || cfg.applePage)}" target="_blank" rel="noopener">${esc(T('erreurLien'))}</a></p>
  </div>
</li>`;
}

function bindRow(li, ep, fmt) {
  const q = (s) => li.querySelector(s);
  const ui = { row: li, ep, fmt, speed: 1, play: q('.pod-play'), panel: q('.pod-ep__panel'), range: q('.pod-range'), cur: q('.pod-time--cur'), tot: q('.pod-time--tot'), error: q('.pod-error'), speedBtn: q('.pod-tool--speed') };
  const paintSpeed = () => { ui.speedBtn.textContent = fmt.fmtSpeed(ui.speed); ui.speedBtn.setAttribute('aria-label', fmt.T('vitesse', { v: fmt.fmtSpeed(ui.speed) })); };
  paintSpeed();
  setBtnState(ui, false);
  ui.play.addEventListener('click', () => toggle(ui));
  const seekBy = (d) => {
    if (!current || current.ui !== ui) activate(ui);
    const go = () => { audio.currentTime = Math.max(0, Math.min((audio.duration || ep.duree_s) - 1, audio.currentTime + d)); };
    if (audio.readyState) go(); else audio.addEventListener('loadedmetadata', go, { once: true });
  };
  q('.pod-tool--back').addEventListener('click', () => seekBy(-15));
  q('.pod-tool--fwd').addEventListener('click', () => seekBy(30));
  ui.speedBtn.addEventListener('click', () => { ui.speed = SPEEDS[(SPEEDS.indexOf(ui.speed) + 1) % SPEEDS.length]; if (current && current.ui === ui) audio.playbackRate = ui.speed; paintSpeed(); });
  ui.range.addEventListener('input', () => { if (current && current.ui === ui) audio.currentTime = +ui.range.value; ui.range.style.setProperty('--pod-p', (ui.range.max > 0 ? (ui.range.value / ui.range.max) * 100 : 0) + '%'); });
  ui.range.addEventListener('keydown', (ev) => { if (ev.key === ' ') { ev.preventDefault(); toggle(ui); } });
  li._ui = ui;
}

function readConfig(host) {
  const node = host.querySelector('script[data-pod-config]');
  if (!node) return null;
  try { return JSON.parse(node.textContent); } catch (e) { return null; }
}

async function init(host) {
  if (host.dataset.ready) return;
  const cfg = readConfig(host);
  const view = host.querySelector('.pod-view');
  if (!cfg || !view) return;
  host.dataset.ready = '1';
  const fmt = makeFormat(cfg);
  const compact = cfg.mode === 'compact';
  const count = +cfg.count || (compact ? 3 : 5);
  const step = +cfg.step || 5;
  const max = +cfg.max || 15;
  const fallback = (cfg.episodes || []).map((e) => ({ ...e, numero: normaliseNumber(e.numero) }));

  let all = fallback.slice(0, max);
  let shown = Math.min(count, all.length);

  const render = () => {
    if (current) deactivate(false);
    const ol = document.createElement('ol');
    ol.className = 'pod-list';
    ol.innerHTML = all.slice(0, shown).map((e, i) => rowHtml(e, i, !compact && i === 0, compact, fmt, cfg)).join('');
    ol.querySelectorAll('.pod-ep').forEach((li) => bindRow(li, all[+li.dataset.i], fmt));
    view.replaceChildren(ol);
    if (!compact && shown < all.length) {
      const wrap = document.createElement('div');
      wrap.className = 'pod-more-wrap';
      wrap.innerHTML = `<button type="button" class="pod-more">${esc(fmt.T('voirPlus'))}</button>`;
      wrap.querySelector('.pod-more').addEventListener('click', () => { const keep = shown; shown = Math.min(all.length, shown + step); appendRows(keep); });
      view.append(wrap);
    }
  };
  const appendRows = (from) => {
    const ol = view.querySelector('.pod-list');
    const frag = document.createElement('div');
    frag.innerHTML = all.slice(from, shown).map((e, k) => rowHtml(e, from + k, false, compact, fmt, cfg)).join('');
    const rows = [...frag.children];
    rows.forEach((li) => { ol.append(li); bindRow(li, all[+li.dataset.i], fmt); });
    if (rows[0]) rows[0].querySelector('.pod-play').focus({ preventScroll: false });
    if (shown >= all.length) view.querySelector('.pod-more-wrap')?.remove();
  };

  /* 1. Liste de secours : lecteur actif tout de suite (même contenu que le rendu serveur) */
  render();
  host.dataset.source = 'json';

  /* 2. Flux RSS : remplace la liste dès qu'il est lu (sans toucher à un épisode en cours de lecture) */
  if (cfg.feed) {
    view.setAttribute('aria-busy', 'true');
    const data = await loadEpisodes(cfg, fallback).catch(() => ({ list: fallback, source: 'json' }));
    view.removeAttribute('aria-busy');
    if (data.source === 'feed' && data.list.length) {
      all = data.list.slice(0, max);
      shown = Math.min(Math.max(count, shown), all.length);
      host.dataset.source = 'feed';
      if (!current) render();
    }
  }
}

const boot = (scope = document) => scope.querySelectorAll('[data-podcast]').forEach(init);
boot();
document.addEventListener('mi:sections-ready', () => boot());
document.addEventListener('shopify:section:load', (e) => boot(e.target));
export { boot as initPodcast };
